import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormControl } from '@angular/forms'
import parsePhoneNumberFromString, { isValidPhoneNumber, AsYouType, CountryCode } from 'libphonenumber-js/max'
import { Subject, takeUntil } from 'rxjs'
import { BasicInputComponent } from '../basic-input/basic-input.component'
import { AppService } from '@app/v2/shared/services/app.service'
import { Country } from '@app/v2/shared/types/country.type'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'

@Component({
  selector: 'phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: [],
  standalone: true,
  imports: [BasicInputComponent]
})
export class PhoneInputComponent implements OnInit, OnDestroy {
  /**
   * @property control FormControl for the input
   */
  @Input() control!: FormControl
  /**
   * @property label for the input
   */
  @Input() label: string = 'Your phone number'
  /**
   * @property id for the input
   */
  @Input() id!: string
  /**
   * @property icon for the input
   */
  @Input()
  icon: string = `<svg class='tw-w-6 tw-h-6 tw-text-gray-800 dark:tw-text-white' aria-hidden='true' xmlns='http://www.w3.org/2000/svg' fill='currentColor' viewBox='0 0 19 18'>
		<path d='M18 13.446a3.02 3.02 0 0 0-.946-1.985l-1.4-1.4a3.054 3.054 0 0 0-4.218 0l-.7.7a.983.983 0 0 1-1.39 0l-2.1-2.1a.983.983 0 0 1 0-1.389l.7-.7a2.98 2.98 0 0 0 0-4.217l-1.4-1.4a2.824 2.824 0 0 0-4.218 0c-3.619 3.619-3 8.229 1.752 12.979C6.785 16.639 9.45 18 11.912 18a7.175 7.175 0 0 0 5.139-2.325A2.9 2.9 0 0 0 18 13.446Z'/>
	</svg>`
  /**
   * @property iconType for the icon of the input
   */
  @Input() iconType: string = 'fill'
  /**
   * @property placeholder for the input
   */
  @Input() placeholder: string = 'xxx xxxxxxxx'
  /**
   * @property countries array of countries objects
   */
  @Input() countries!: Country[]
  /**
   * @property getLocation boolean to get user location
   */
  @Input() getLocation: boolean = true
  /**
   * @property required flag to indicate if the field is required
   */
  @Input() required: boolean = false
  /**
   * @property mode for the input
   */
  @Input() mode: BasicInputMode = BasicInputMode.DEFAULT
  /**
   * @property allowClearButton flag to indicate if the clear button is allowed
   */
  @Input() allowClearButton: boolean = true

  /**
   * @property valueChanged event emitter for the input
   */
  @Output() onInputBlur: EventEmitter<string> = new EventEmitter<string>()
  @Output() onKeydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>()
  @Output() onBlurValueChange: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>()

  public basicFieldType = BasicFieldType.PHONE
  private _phoneNumberCountry: string = ''
  private _lastUsedCountry!: Country
  private _destroy$ = new Subject()
  private _asYouType = new AsYouType()

  /**
   * @method Initialize Component
   * @param _appService AppService
   */
  constructor(private _appService: AppService) {}

  ngOnInit(): void {
    this.setCountryCode()
    this.control.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      if (!value && this.control.pristine) this.setCountryCode()
    })

    // Format the existing phone number value when component initializes
    if (this.control.value) this._formatExistingPhoneNumber()
    if (this._appService.countries?.length) this.countries = this._appService.countries
    else
      this._appService
        .getAllCountries()
        .pipe(takeUntil(this._destroy$))
        .subscribe((response) => {
          this.countries = response.data.countries
        })
  }

  /**
   * @method Destroy Component and unsubscribe from all subscriptions
   * @returns void
   */
  ngOnDestroy(): void {
    this._destroy$.next('')
    this._destroy$.complete()
  }

  /**
   * Pre-set the country code from the cached country
   */
  public setCountryCode() {
    if (this.control.value || !this.getLocation) return
    const cachedCountry = this._appService.getCachedUserCountry()
    if (cachedCountry) {
      this._lastUsedCountry = cachedCountry
      this.control.patchValue(this._lastUsedCountry?.country_code)
      this.control.markAsPristine()
      this.control.markAsUntouched()
    }
  }

  /**
   * @property Get flag Image based on phone code
   * @returns string
   */
  public get getImage(): string {
    const country = this.countries?.find(
      (country: Country) =>
        (country.country_code === this.control?.value?.split(' ')[0] && this._phoneNumberCountry === country.iso2) ||
        (!this._phoneNumberCountry && country.country_code === this.control?.value?.split(' ')[0])
    )
    if (country?.flag) this._lastUsedCountry = country
    // Update the last used country if we found a match
    if (country?.flag) this._lastUsedCountry = country

    if (country?.flag && this.control.value) return country.flag
    else if (this._lastUsedCountry?.flag && this.control.value?.length >= 3) return this._lastUsedCountry.flag
    else return ''
  }

  /**
   * @method Validate phone number
   * @returns void
   */
  public validateNumber(): void {
    const formattedPhoneNumber = this.control.value.replaceAll(/\s/g, '')
    const countryCode = this._getCountryAbbreviation(formattedPhoneNumber)

    if (this._isJustCountryCode(formattedPhoneNumber)) {
      this.control.setErrors({ invalidNumber: true })
      return
    }

    this._validatePhoneNumber(formattedPhoneNumber, countryCode)
  }

  /**
   * @description Get the phone number country
   * @param cleanNumber string
   * @returns string | undefined
   */
  private _getCountryAbbreviation(cleanNumber: string): string | undefined {
    const phoneNumberCountry = parsePhoneNumberFromString(`+${cleanNumber}`)?.country
    this._phoneNumberCountry = phoneNumberCountry || ''
    return this._phoneNumberCountry
  }

  /**
   * @description Check if the field is just a country code
   * @param cleanNumber string
   * @returns boolean
   */
  private _isJustCountryCode(cleanNumber: string): boolean {
    const isJustCountryCode = this.countries?.some((country) => `+${country.country_code}` === `+${cleanNumber}`)

    return isJustCountryCode
  }

  /**
   * @description Validate the full phone number
   * @param cleanNumber string
   * @param phoneNumberCountry string | undefined
   */
  private _validatePhoneNumber(cleanNumber: string, phoneNumberCountry: string | undefined): void {
    const isValid = isValidPhoneNumber(`+${cleanNumber}`, phoneNumberCountry as CountryCode)

    if (!isValid) {
      this.control.setErrors({ invalidNumber: true })
    } else {
      this.control.setErrors(null)
    }
  }

  /**
   * @method Check if the current value is valid for a required field
   * @returns boolean
   */
  private _isValidForRequiredField(): boolean {
    if (!this.required) return true

    const value = this.control.value
    if (!value || value.trim() === '') return false

    const cleanValue = value.replace(/\s+/g, '')
    return !this._isJustCountryCode(cleanValue)
  }

  /**
   * Emits control value when blur event is fired
   * @returns void
   */
  public onBlur(): void {
    this.control.markAsTouched()
    if (!this.control.value) {
      this.control.setErrors(null)
      this.control.updateValueAndValidity()
    } else {
      // If it's required but only country code is entered, set the invalidNumber error
      if (this.required && !this._isValidForRequiredField()) this.control.setErrors({ invalidNumber: true })
    }

    this.onInputBlur.emit(this.control.value)
  }

  /**
   * Forward keydown event to parent
   * @param {KeyboardEvent} event
   */
  public onKeydownEvent(event: KeyboardEvent): void {
    this.onKeydown.emit(event)
  }

  /**
   * Forward blur event to parent
   * @param {FocusEvent} event
   */
  public onBlurEvent(event: FocusEvent): void {
    this.setCountryCode()
    this.onBlurValueChange.emit(event)
  }

  /**
   * @method Restore cursor position directly using the input element
   * @param inputElement HTMLInputElement
   * @param oldValue string
   * @param newValue string
   * @param oldCursorPosition number
   * @returns void
   */
  private _restoreCursorPositionDirectly(inputElement: HTMLInputElement, oldValue: string, newValue: string, oldCursorPosition: number): void {
    //if value got longer, move cursor forward; if shorter, move back
    const lengthDiff = newValue.length - oldValue.length
    const newPosition = Math.max(0, Math.min(newValue.length, oldCursorPosition + lengthDiff))

    inputElement.setSelectionRange(newPosition, newPosition)
    inputElement.focus()
  }

  /**
   * @method Detect Country when pasting a number
   * @param event Event
   * @returns void
   */
  public pasteNumber(event: Event): void {
    const inputElement = event.target as HTMLInputElement
    if (!inputElement.value) {
      this.control.setErrors(null)
      return
    }

    // Clean the pasted value
    let value = inputElement.value.replace(/\s+/g, '').replace(/^00/, '').replace(/^0/, '')

    if (value.startsWith('+')) {
      value = value.substring(1)
    }

    // Detect country from the number
    this.countries.forEach((country) => {
      if (value.startsWith(country.country_code)) {
        this._lastUsedCountry = country
      }
    })

    // Format the pasted value
    this._sanitizeAndValidatePhoneNumber(value, undefined, inputElement)

    // Check if the pasted value is valid for a required field
    if (this.required && !this._isValidForRequiredField()) this.control.setErrors({ invalidNumber: true })
  }

  /**
   * @method Stop user from adding Leading Zeros
   * @param event Event
   * @returns void
   */
  public checkLeadingZeros(event: Event): void {
    const inputElement = event.target as HTMLInputElement
    if (!inputElement.value) return

    if (inputElement.value[0] === '0') this.control.setValue(this.control.value.replace(/^0+/g, ''))
  }

  /**
   * @method Handle input events for real-time formatting
   * @param event Event
   * @returns void
   */
  public onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement
    if (!inputElement) return

    // Get the current cursor position
    const cursorPosition = inputElement.selectionStart || 0
    this._sanitizeAndValidatePhoneNumber(inputElement.value, cursorPosition, inputElement)
    this._handleRemoveCountryCode(event)
  }

  /**
 /**
   * @description Sanitizes and validates phone number input
   * @param {string} inputValue
   * @param {number} cursorPosition
   * @param {HTMLInputElement} inputElement
   * @returns {void}
   */
  private _sanitizeAndValidatePhoneNumber(inputValue: string, cursorPosition?: number, inputElement?: HTMLInputElement): void {
    if (!inputValue) return

    // Clean the input value (remove any malformed characters)
    let cleanValue = inputValue.replace(/[^0-9\s]/g, '')
    // Reset AsYouType instance for clean formatting
    this._asYouType.reset()

    // Format the input value
    const formattedValue = this._asYouType.input('+' + cleanValue).replace('+', '')

    // Only update if formatting changed something
    if (formattedValue !== inputValue) {
      // Store the old value for cursor calculation
      const oldValue = this.control.value

      // Update control value without triggering additional events
      this.control.setValue(formattedValue, { emitEvent: false })

      // Restore cursor position after formatting using the input element directly
      if (cursorPosition !== undefined && inputElement) this._restoreCursorPositionDirectly(inputElement, oldValue, formattedValue, cursorPosition)
    }
    this.validateNumber()
  }

  /**
   * @method Format existing phone number when component initializes
   * @returns void
   */
  private _formatExistingPhoneNumber(): void {
    if (!this.control.value) return

    // Remove any existing spaces to ensure consistent formatting
    const rawNumber = this.control.value.replace(/\s+/g, '')
    // Format the number using AsYouType
    const formattedNumber = new AsYouType().input(`+${rawNumber}`)

    this.control.setValue(formattedNumber.replace('+', ''), {
      emitEvent: false
    })
  }

  /**
   * @method Handle remove country code
   * @param event Event
   * @returns void
   */
  private _handleRemoveCountryCode(event: Event): void {
    const inputElement = event.target as HTMLInputElement
    if (inputElement.value[0] === '0') this.control.setValue(this.control.setValue(''))
  }
}
